import React, { useContext } from 'react';
import classNames from 'classnames';
import {
    UncontrolledCollapse,
    Badge,
    Card,
    CardHeader,
    CardBody,
    Nav,
    NavItem,
    NavLink,
    Alert,
} from 'reactstrap';
import { UserContext } from '../contexts/UserContext';
import { hasRolePermission } from '../utility-services/rolePermissions';
import SimpleBar from 'simplebar-react';
import checkBold from '@iconify/icons-mdi/check-bold';
import closeThick from '@iconify/icons-mdi/close-thick';
import clockMinusOutline from '@iconify/icons-mdi/clock-minus-outline';
import numeric1CircleOutline from '@iconify/icons-mdi/numeric-1-circle-outline';
import { GrChatOption } from 'react-icons/gr';
import AuthorizationDetailView from './TableRowDetailContent/AuthorizationDetailView';
import {
    ActionButton,
    ActionCommentModal,
    ActionSendModal,
    ActionDoNotSendModal,
    ActionQuestionModal,
    ActionLimitedApprovalModal,
} from './TableRowDetailContent/ActionControls';
import { decode } from 'he';
import { getAlertCommentsForDisplay } from './TableRowDetailSharedUtils';

const toTitleCase = (value) =>
    String(value)
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

function renderEmphasizedLines(text) {
    const lines = String(text || '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line !== '');
    if (!lines.length) return <b></b>;

    const elements = [];
    lines.forEach((line, lineIdx) => {
        if (lineIdx > 0) elements.push(<br key={`br-${lineIdx}`} />);

        const segments = [];
        const emphasisPattern = /\*+([^*]+)\*+/g;
        let lastIndex = 0;
        let match;
        while ((match = emphasisPattern.exec(line)) !== null) {
            if (match.index > lastIndex) {
                segments.push({ text: line.slice(lastIndex, match.index), emphasis: false });
            }
            segments.push({ text: match[1].trim(), emphasis: true });
            lastIndex = emphasisPattern.lastIndex;
        }
        if (lastIndex < line.length) {
            segments.push({ text: line.slice(lastIndex), emphasis: false });
        }

        elements.push(
            <b key={lineIdx}>
                {segments.map((seg, segIdx) => (
                    <span key={segIdx} style={seg.emphasis ? { color: '#c10007' } : undefined}>
                        {seg.text}
                    </span>
                ))}
            </b>
        );
    });
    return elements;
}

const TableRowDetailBody = ({
    alert,
    stateName,
    category,
    activeTab,
    setActiveTab,
    requestActionPermissionWidgetName,
    pharmacies,
    getPermittedActions,
    getDateTime,
    getDateAndTime,
    getDateAndTimeStamp,
    capitalizeFirstLetter,
    getUserActionLabelForHeader,
    getLastAlertStage,
    checkAlertStatus,
    checkQuantity,
    checkSupply,
    showNonMediprocityUserAlert,
    handleActionButtons,
    commentModalInfo,
    questionModalInfo,
    limitedApprovalModalInfo,
    sendModalInfo,
    doNotSendModalInfo,
    toggleCommentModal,
    toggleQuestionModal,
    toggleLimitedApprovalModal,
    toggleSendModal,
    toggleDoNotSendModal,
    handleUserAction,
    loadingUserAction,
    actionWidgetNames = {},
}) => {
    const { roles, user } = useContext(UserContext);
    const hasRoleData = Array.isArray(roles) && roles.length > 0;

    // Only the roles held on the alert's own facility (pharmacy users: its pharmacy; SysAdmin counts everywhere),
    // the same roles the server checks when the action is sent
    const getRolesForAlert = (pharmId, facID) => {
        const entityRoles = user?.entitiesAndRoles;
        if (!Array.isArray(entityRoles) || entityRoles.length === 0) return roles;
        const isPharmacyUser = user?.userType === 'PharmacyUser';
        const entityId = isPharmacyUser ? pharmId : facID;
        const roleNames = new Set(
            entityRoles
                .filter((er) => er.entityID === entityId || (isPharmacyUser && er.roleName === 'SysAdmin'))
                .map((er) => er.roleName)
        );
        return roles.filter((role) => roleNames.has(role.name));
    };

    const discussChatIcon = <GrChatOption size={22} />;
    const resolvedActionWidgetNames = {
        pumpPickup: 'PumpPickup',
        refillTooSoon: 'RefillTooSoon',
        narcoticsDiscuss: category === 'alfNewScript' ? 'alfNewScript' : 'NarcoticsRefill',
        ...actionWidgetNames,
    };

    const isLimitApprovalUnavailable = Number(alert?.minQtyDisp) > 0 && Number(alert?.qty) === Number(alert?.minQtyDisp);
    const LIMIT_APPROVAL_DISABLED_TITLE = 'Limited Approval not available for this drug';

    const highCostActionButtons = [
        { icon: checkBold, title: 'Approve', action: 'approve', disable: false },
        { icon: closeThick, title: 'Deny', action: 'deny', disable: false },
        {
            icon: clockMinusOutline,
            title: isLimitApprovalUnavailable ? LIMIT_APPROVAL_DISABLED_TITLE : 'Limit Days',
            action: 'submitlimit',
            disable: isLimitApprovalUnavailable,
        },
        { icon: numeric1CircleOutline, title: 'Approve Once', action: 'approveonce', disable: false },
        { icon: discussChatIcon, title: 'Discuss', action: 'ask', disable: false },
    ];

    const highCostAlertsActionButtons = [
        { icon: checkBold, title: 'Approve', action: 'approve', disable: false },
        { icon: closeThick, title: 'Deny', action: 'deny', disable: false },
        {
            icon: clockMinusOutline,
            title: isLimitApprovalUnavailable ? LIMIT_APPROVAL_DISABLED_TITLE : 'Limit Days',
            action: 'submitlimit',
            disable: isLimitApprovalUnavailable,
        },
        { icon: discussChatIcon, title: 'Discuss', action: 'ask', disable: false },
    ];

    const clarificationsActionButtons = [{ icon: discussChatIcon, title: 'Discuss', action: 'ask', disable: false }];
    const remainingWidgetsActionButtons = [{ icon: discussChatIcon, title: 'Discuss', action: 'ask', disable: false }];
    const archivedActionButtons = [{ icon: discussChatIcon, title: 'Discuss', action: 'ask', disable: false }];
    const admissionMedicationReviewActionButtons = [{ icon: discussChatIcon, title: 'Discuss', action: 'ask', disable: false }];
    const RTSActionButtons = [
        { icon: checkBold, title: 'Approve until Refill Due', action: 'limitedApproval', disable: false },
        { icon: discussChatIcon, title: 'Discuss', action: 'ask', disable: false },
    ];
    const pumpPickupActionButtons = [{ icon: discussChatIcon, title: 'Discuss', action: 'ask', disable: false }];
    const narcoticsRefillActionButtons = [{ icon: discussChatIcon, title: 'Discuss', action: 'ask', disable: false }];

    const checkActionPermissions = (requestActions, widgetName, _pharmacies, pharmId, facID) => (
        getPermittedActions ? getPermittedActions(requestActions, widgetName, { pharmID: pharmId, facID }) : requestActions
    );

    const TIER_CAPABILITY_KEY = {
        high_cost: { fullAccess: 'canFullAccessHighCost', ask: 'canAskHighCost' },
        high_cost_alerts: { fullAccess: 'canFullAccessHighCostAlerts', ask: 'canAskHighCostAlerts' },
        non_covered: { fullAccess: 'canFullAccessNonCovered', ask: 'canAskNonCovered' },
        prior_auth: { fullAccess: 'canFullAccessPriorAuth', ask: 'canAskPriorAuth' },
        therapeutic_interchange: { fullAccess: 'canFullAccessTherapeuticInterchange', ask: 'canAskTherapeuticInterchange' },
        admission_meds: { fullAccess: 'canFullAccessAdmissionMeds', ask: 'canAskAdmissionMeds' },
        open_batch: { fullAccess: 'canFullAccessOpenBatch', ask: 'canAskOpenBatch' },
        refill_too_soon: { fullAccess: 'canFullAccessRefillTooSoon', ask: 'canAskRefillTooSoon' },
        pump_pickup: { fullAccess: 'canFullAccessPumpPickup', ask: 'canAskPumpPickup' },
        alf_new_script: { fullAccess: 'canFullAccessAlfNewScript', ask: 'canAskAlfNewScript' },
        narcotics_refill: { fullAccess: 'canFullAccessNarcoticsRefill', ask: 'canAskNarcoticsRefill' },
        specialty_med_or_not_available: {
            fullAccess: 'canFullAccessSpecialtyMedOrNotAvailable',
            ask: 'canAskSpecialtyMedOrNotAvailable',
        },
    };

    const getActionButtonsByRolePermission = (
        permissionPrefix,
        baseButtons,
        pharmId,
        facID,
        legacyWidgetName = requestActionPermissionWidgetName
    ) => {
        if (!hasRoleData) {
            return checkActionPermissions(baseButtons, legacyWidgetName, pharmacies, pharmId, facID);
        }
        const tier = TIER_CAPABILITY_KEY[permissionPrefix];
        const alertRoles = getRolesForAlert(pharmId, facID);
        if (tier?.fullAccess && hasRolePermission(alertRoles, tier.fullAccess)) return baseButtons;
        if (tier?.ask && hasRolePermission(alertRoles, tier.ask)) return baseButtons.filter((button) => button.action === 'ask');
        return [];
    };

    const getHighCostActionButtons = (baseButtons, pharmId, facID) =>
        getActionButtonsByRolePermission('high_cost', baseButtons, pharmId, facID);

    const isHighCostLabelPrintedStage = ['Label Printed On', 'Packed', 'Checked', 'Placed In Tote'].includes(
        getLastAlertStage(alert)
    );

    const getNarcoticsStyleDiscussButtons = (pharmID, facID) => {
        const permissionWidget = category === 'alfNewScript' ? 'narcoticsRefill' : requestActionPermissionWidgetName;
        const permissionPrefix = category === 'alfNewScript' ? 'alf_new_script' : 'narcotics_refill';
        if (!hasRoleData) {
            const permitted = checkActionPermissions(narcoticsRefillActionButtons, permissionWidget, pharmacies, pharmID, facID);
            return permitted?.length ? permitted : narcoticsRefillActionButtons;
        }
        return getActionButtonsByRolePermission(permissionPrefix, narcoticsRefillActionButtons, pharmID, facID);
    };

    return (
        <>
            <div style={{ backgroundColor: 'none' }}>
                <Nav tabs className="nav-justified nav-bordered mb-2">
                    <NavItem>
                        <NavLink
                            style={{ backgroundColor: 'transparent' }}
                            href="#"
                            className={classNames('pt-0', { active: activeTab === 'detail' })}
                            onClick={() => {
                                if (activeTab === 'actions') setActiveTab('detail');
                            }}>
                            <span>Detail</span>
                        </NavLink>
                    </NavItem>
                    {stateName !== 'alfCostResponseList' && (
                        <NavItem>
                            <NavLink
                                style={{ backgroundColor: 'transparent' }}
                                href="#"
                                className={classNames('pt-0', { active: activeTab === 'actions' })}
                                onClick={() => {
                                    if (activeTab === 'detail') setActiveTab('actions');
                                }}>
                                <span>
                                    Actions/Discussions{' '}
                                    <Badge color="danger" className="ml-1">
                                        {alert?.hasOwnProperty('requestedActions') ? alert?.requestedActions?.length : 0}
                                    </Badge>
                                </span>
                            </NavLink>
                        </NavItem>
                    )}
                </Nav>
            </div>
                {stateName === 'highCostList' && (
                    <AuthorizationDetailView
                        alert={alert}
                        activeTab={activeTab}
                        category={category}
                        getDateTime={getDateTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        getUserActionLabelForHeader={getUserActionLabelForHeader}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        renderDetailContent={(a) => (
                            <>
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <p>RX Number: </p>
                                        <span><b>{a?.rxNo}</b></span>
                                    </div>
                                    <div>
                                        <p>NDC:</p>
                                        <span><b>{a?.ndc}</b></span>
                                    </div>
                                    <div>
                                        <p>Dispense Date:</p>
                                        <span><b>{getDateTime(a?.dispenseDt)}</b></span>
                                    </div>
                                    <div>
                                        <p>Alerted On:</p>
                                        <span><b>{getDateTime(a?.alertCreatedOn)}</b></span>
                                        <span className="ml-2"><b>{getDateAndTimeStamp(a?.alertCreatedOn)}</b></span>
                                    </div>
                                </div>
                                <hr />
                                <div>
                                    Facility Name: <b>({a?.facID}) {a?.facName}</b>
                                </div>
                                <div>
                                    Patient Name: <b>{a?.patName}</b>
                                </div>
                                <div>
                                    <span>Nursing Station: <b>{a?.nsID}</b></span>
                                    <span className="ml-2">Room: <b>{a?.room}</b></span>
                                    <span className="ml-2">Bed: <b>{a?.bed}</b></span>
                                </div>
                                {a?.authBy && (
                                    <div>
                                        Authorized By: <b>{a.authBy}</b>
                                    </div>
                                )}
                                <hr />
                                <div>
                                    Drug Label Name: <b>{a?.drugLabelName}</b>
                                </div>
                                 <div>
                                    Directions: <b>{a?.sig}</b>
                                </div>
                                <div>
                                    Qty: <b>{a?.qty}</b>
                                </div>
                                {a?.minQtyDisp > 0 && (
                                    <div>
                                        Min Drug Qty: <b>{a.minQtyDisp}</b>
                                    </div>
                                )}
                                <div>
                                    Days Supply: <b>{a?.daysSupply}</b>
                                </div>
                                <div>
                                    Total Price: <b>${a?.ttlPrice}</b>
                                </div>
                                {a?.invoiceGrp && (
                                    <div>
                                        Invoice Group: <b>{a.invoiceGrp}</b>
                                    </div>
                                )}
                                {(a?.srxStatus || a?.pharmacyStatus) && <hr />}
                                {a?.srxStatus && (
                                    <h4 className="my-1">Resolution: {getUserActionLabelForHeader(a.srxStatus)}</h4>
                                )}
                                {a?.pharmacyStatus && (
                                    <small>
                                        Pharmacy Resolution: <b>{a.pharmacyStatus}</b>
                                    </small>
                                )}
                                <hr />
                                {showNonMediprocityUserAlert && (
                                    <Alert color="danger" className="alert-link">
                                        SpecialtyRx Now credentials are required for user actions <br /> <br />
                                        <a href="/settings/specialtyRxNow">Set SpecialtyRx Now credentials</a>
                                    </Alert>
                                )}
                                <div className="bg-dark d-flex justify-content-end shadow" style={{ height: '45px', borderRadius: '3px' }}>
                                    {getHighCostActionButtons(
                                        a?.hasOwnProperty('_internalArchiveID') && a._internalArchiveID > 0
                                            ? archivedActionButtons
                                            : highCostActionButtons,
                                        a?.pharmID,
                                        a?.facID
                                    ).map((button, i) => (
                                        <ActionButton
                                            key={i}
                                            handleOnClick={handleActionButtons}
                                            icon={button.icon}
                                            title={button.title}
                                            index={i + 'detail'}
                                            action={button.action}
                                            disabled={button.disable}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                        renderDetailActionBar={() => (
                            getHighCostActionButtons(
                                alert?.hasOwnProperty('_internalArchiveID') && alert._internalArchiveID > 0
                                    ? archivedActionButtons
                                    : highCostActionButtons,
                                alert?.pharmID,
                                alert?.facID
                            ).map((button, i) => (
                                <ActionButton
                                    key={i}
                                    handleOnClick={handleActionButtons}
                                    icon={button.icon}
                                    title={button.title}
                                    index={i + 'detail'}
                                    action={button.action}
                                    disabled={button.disable}
                                />
                            ))
                        )}
                        renderActionsBar={() => (
                            <>
                                {getHighCostActionButtons(
                                    alert?.hasOwnProperty('_internalArchiveID') && alert._internalArchiveID > 0
                                        ? archivedActionButtons
                                        : highCostActionButtons,
                                    alert?.pharmID,
                                    alert?.facID
                                ).map((button, i) => (
                                    <ActionButton
                                        key={i}
                                        handleOnClick={handleActionButtons}
                                        icon={button.icon}
                                        title={button.title}
                                        index={i}
                                        action={button.action}
                                        disabled={button.disable}
                                    />
                                ))}
                                <ActionLimitedApprovalModal
                                    isOpen={limitedApprovalModalInfo.isOpen}
                                    action={limitedApprovalModalInfo.action}
                                    toggle={toggleLimitedApprovalModal}
                                    color={limitedApprovalModalInfo.color}
                                    label={limitedApprovalModalInfo.label}
                                    submitAction={handleUserAction}
                                    loading={loadingUserAction}
                                    widget={'HighCost'}
                                />
                                <ActionQuestionModal
                                    isOpen={questionModalInfo.isOpen}
                                    action={questionModalInfo.action}
                                    toggle={toggleQuestionModal}
                                    color={questionModalInfo.color}
                                    label={questionModalInfo.label}
                                    submitAction={handleUserAction}
                                    loading={loadingUserAction}
                                    widget={'HighCost'}
                                />
                                <ActionCommentModal
                                    isOpen={commentModalInfo.isOpen}
                                    action={commentModalInfo.action}
                                    toggle={toggleCommentModal}
                                    color={commentModalInfo.color}
                                    label={commentModalInfo.label}
                                    submitAction={handleUserAction}
                                    loading={loadingUserAction}
                                    widget={'HighCost'}
                                    requireUsername
                                />
                            </>
                        )}
                    />
                )}
                {stateName === 'highCostAlertsListFlat' && (
                    <AuthorizationDetailView
                        alert={alert}
                        activeTab={activeTab}
                        category={category}
                        getDateTime={getDateTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        getUserActionLabelForHeader={getUserActionLabelForHeader}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        variant="flat"
                        getLastAlertStage={getLastAlertStage}
                        checkAlertStatus={checkAlertStatus}
                        checkQuantity={checkQuantity}
                        checkSupply={checkSupply}
                        renderDetailActionBar={() =>
                            getActionButtonsByRolePermission(
                                'high_cost_alerts',
                                alert?.hasOwnProperty('_internalArchiveID') && alert._internalArchiveID > 0 && alert._internalArchiveID !== 0
                                    ? archivedActionButtons
                                    : highCostAlertsActionButtons,
                                alert?.pharmID,
                                alert?.facID,
                                'highCostAlert'
                            ).map((button, i) => (
                                <ActionButton
                                    key={i}
                                    handleOnClick={handleActionButtons}
                                    icon={button.icon}
                                    title={isHighCostLabelPrintedStage && button.action !== 'ask' ? 'Disabled - Contact Pharmacy' : button.title}
                                    index={i + 'detail'}
                                    action={button.action}
                                    disabled={(isHighCostLabelPrintedStage && button.action !== 'ask') || button.disable}
                                />
                            ))
                        }
                        renderActionsBar={() => (
                            <>
                                {getActionButtonsByRolePermission(
                                    'high_cost_alerts',
                                    alert?.hasOwnProperty('_internalArchiveID') && alert._internalArchiveID > 0 && alert._internalArchiveID !== 0
                                        ? archivedActionButtons
                                        : highCostAlertsActionButtons,
                                    alert?.pharmID,
                                    alert?.facID,
                                    'highCostAlert'
                                ).map((button, i) => (
                                    <ActionButton
                                        key={i}
                                        handleOnClick={handleActionButtons}
                                        icon={button.icon}
                                        title={isHighCostLabelPrintedStage && button.action !== 'ask' ? 'Disabled - Contact Pharmacy' : button.title}
                                        index={i}
                                        action={button.action}
                                        disabled={(isHighCostLabelPrintedStage && button.action !== 'ask') || button.disable}
                                    />
                                ))}
                                <ActionLimitedApprovalModal
                                    isOpen={limitedApprovalModalInfo.isOpen}
                                    action={limitedApprovalModalInfo.action}
                                    toggle={toggleLimitedApprovalModal}
                                    color={limitedApprovalModalInfo.color}
                                    label={limitedApprovalModalInfo.label}
                                    submitAction={handleUserAction}
                                    loading={loadingUserAction}
                                    widget={'highcostalerts'}
                                />
                                <ActionQuestionModal
                                    isOpen={questionModalInfo.isOpen}
                                    action={questionModalInfo.action}
                                    toggle={toggleQuestionModal}
                                    color={questionModalInfo.color}
                                    label={questionModalInfo.label}
                                    submitAction={handleUserAction}
                                    loading={loadingUserAction}
                                    widget={'highcostalerts'}
                                />
                                <ActionCommentModal
                                    isOpen={commentModalInfo.isOpen}
                                    action={commentModalInfo.action}
                                    toggle={toggleCommentModal}
                                    color={commentModalInfo.color}
                                    label={commentModalInfo.label}
                                    submitAction={handleUserAction}
                                    loading={loadingUserAction}
                                    widget={'highcostalerts'}
                                    requireUsername
                                />
                            </>
                        )}
                    />
                )}
                {stateName === 'nonCoveredList' && (
                    <AuthorizationDetailView
                        alert={alert}
                        activeTab={activeTab}
                        category={category}
                        getDateTime={getDateTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        renderDetailContent={(a) => (
                            <>
                                <div className="d-flex justify-content-between">
                                    <div><p>RX Number: </p><span><b>{a.rxNo}</b></span></div>
                                    <div />
                                    <div><p>Alerted On:</p><span><b>{getDateTime(a.alertCreatedOn)}</b></span><span className="ml-2"><b>{getDateAndTimeStamp(a.alertCreatedOn)}</b></span></div>
                                </div>
                                <hr />
                                <div>Facility Name: <b>({a.facID}) {a.facName}</b></div>
                                <div>Patient Name: <b>{a.patName}</b></div>
                                <div><span>Nursing Station: <b>{a.nsID}</b></span> <span className="ml-2">Room: <b>{a.room}</b></span> <span className="ml-2">Bed: <b>{a.bed}</b></span></div>
                                <hr />
                                <div>Drug Label Name: <b>{a.drugLabelName}</b></div>
                                <div>Qty: <b>{a.qty}</b></div>
                                <div>Days Supply: <b>{a.daysSupply}</b></div>
                                <hr />
                                <div>
                                    Comments:{' '}
                                    {getAlertCommentsForDisplay(a).map((comment, i) => (
                                        <div key={i}>
                                            {i > 0 && <hr className="mx-0 p-0 w-25" />}
                                            {comment.reason ? (
                                                <div>
                                                    <b>{comment.reason}</b>
                                                </div>
                                            ) : null}
                                            <div><small><i>{getDateAndTime(comment.loggedAt)}</i></small></div>
                                            <div><b>{comment.comment}</b></div>
                                        </div>
                                    ))}
                                </div>
                                <hr />
                                {showNonMediprocityUserAlert && (
                                    <Alert color="danger" className="alert-link">
                                        SpecialtyRx Now credentials are required for user actions <br /> <br />
                                        <a href="/settings/specialtyRxNow">Set SpecialtyRx Now credentials</a>
                                    </Alert>
                                )}
                                <div className="bg-dark d-flex justify-content-end shadow" style={{ height: '45px', borderRadius: '3px' }}>
                                    {getActionButtonsByRolePermission('non_covered', a?.hasOwnProperty('_internalArchiveID') && a._internalArchiveID > 0 ? archivedActionButtons : remainingWidgetsActionButtons, a?.pharmID, a?.facID)?.map((button, i) => (
                                        <ActionButton key={i + 'detail'} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i + 'detail'} action={button.action} disabled={button.disable} />
                                    ))}
                                </div>
                            </>
                        )}
                        renderActionsBar={() => (
                            <>
                                {getActionButtonsByRolePermission('non_covered', alert?.hasOwnProperty('_internalArchiveID') && alert._internalArchiveID > 0 ? archivedActionButtons : remainingWidgetsActionButtons, alert?.pharmID, alert?.facID)?.map((button, i) => (
                                    <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i} action={button.action} disabled={button.disable} />
                                ))}
                                <ActionQuestionModal isOpen={questionModalInfo.isOpen} action={questionModalInfo.action} toggle={toggleQuestionModal} color={questionModalInfo.color} label={questionModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={'NonCovered'} />
                            </>
                        )}
                    />
                )}
                {stateName === 'priorAuthList' && (
                    <AuthorizationDetailView
                        alert={alert}
                        activeTab={activeTab}
                        category={category}
                        getDateTime={getDateTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        renderDetailContent={(a) => (
                            <>
                                <div className="d-flex justify-content-between">
                                    <div><p>RX Number: </p><span><b>{a.rxNo}</b></span></div>
                                    <div><p>Dispense Date:</p><span><b>{getDateTime(a.dispenseDt)}</b></span></div>
                                    <div><p>Alerted On:</p><span><b>{getDateTime(a.alertCreatedOn)}</b></span><span className="ml-2"><b>{getDateAndTimeStamp(a.alertCreatedOn)}</b></span></div>
                                </div>
                                <hr />
                                <div>Facility Name: <b>({a.facID}) {a.facName}</b></div>
                                <div>Patient Name: <b>{a.patName}</b></div>
                                <hr />
                                <div>Drug Label Name: <b>{a.drugLabelName}</b></div>
                                <div>Qty: <b>{a.qty}</b></div>
                                <div>Total Price: <b>${a.ttlPrice}</b></div>
                                <hr />
                                <div className="d-flex">
                                    Status:
                                    <Badge pill style={{ fontSize: '10px', paddingTop: '5px' }} className={`ml-1 badge badge-outline-${a.srxStatus?.toLowerCase() === 'pending' ? 'danger' : 'success'}`}>
                                        {capitalizeFirstLetter(a.srxStatus?.toLowerCase() || '')}
                                    </Badge>
                                </div>
                                <div>Billing Comment: <br /> <b>{a.billingComment}</b></div>
                                <hr />
                               
    {a?.billingComment?.trim() && (
  <div>
    <h4> Comment: </h4>
  </div>
)}                           <div style={{ border: '2px solid #eef2f7', borderRadius: '5px' }}>
                                    <SimpleBar style={{ maxHeight: '330px' }} className="mx-1 px-1">
                                        {getAlertCommentsForDisplay(a).map((comment, i) => (
                                            <React.Fragment key={i}>
                                                <Card className={'mx-1', 'border', 'mb-1', 'shadow-sm', { 'mt-1': i === 0 }}>
                                                    <CardHeader className="bg-light">
                                                        {comment.reason ? (
                                                            <div className="mb-1"><b>{comment.reason}</b></div>
                                                        ) : null}
                                                        <div><small><i>{getDateAndTime(comment.loggedAt)}</i></small></div>
                                                    </CardHeader>
                                                    <CardBody><div>{comment.comment}</div></CardBody>
                                                </Card>
                                            </React.Fragment>
                                        ))}
                                    </SimpleBar>
                                </div>
                                <hr />
                                {showNonMediprocityUserAlert && (
                                    <Alert color="danger" className="alert-link">
                                        SpecialtyRx Now credentials are required for user actions <br /> <br />
                                        <a href="/settings/specialtyRxNow">Set SpecialtyRx Now credentials</a>
                                    </Alert>
                                )}
                                <div className="bg-dark d-flex justify-content-end shadow" style={{ height: '45px', borderRadius: '3px' }}>
                                    {getActionButtonsByRolePermission('prior_auth', a?.hasOwnProperty('_internalArchiveID') && a._internalArchiveID > 0 ? archivedActionButtons : remainingWidgetsActionButtons, a?.pharmID, a?.facID)?.map((button, i) => (
                                        <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i + 'detail'} action={button.action} disabled={button.disable} />
                                    ))}
                                </div>
                            </>
                        )}
                        renderActionsBar={() => (
                            <>
                                {getActionButtonsByRolePermission('prior_auth', alert?.hasOwnProperty('_internalArchiveID') && alert._internalArchiveID > 0 ? archivedActionButtons : remainingWidgetsActionButtons, alert?.pharmID, alert?.facID)?.map((button, i) => (
                                    <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i} action={button.action} disabled={button.disable} />
                                ))}
                                <ActionQuestionModal isOpen={questionModalInfo.isOpen} action={questionModalInfo.action} toggle={toggleQuestionModal} color={questionModalInfo.color} label={questionModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={'PriorAuth'} />
                            </>
                        )}
                    />
                )}
                {stateName === 'therapeuticInterchangeList' && (
                    <AuthorizationDetailView
                        alert={alert}
                        activeTab={activeTab}
                        category={category}
                        getDateTime={getDateTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        renderDetailContent={(a) => (
                            <>
                                <div className="d-flex justify-content-between">
                                    <div><p>RX Number: </p><span><b>{a.rxNo}</b></span></div>
                                    <div><p>Dispense Date:</p><span><b>{getDateTime(a.dispenseDt)}</b></span></div>
                                    <div><p>Alerted On:</p><span><b>{getDateTime(a.alertCreatedOn)}</b></span><span className="ml-2"><b>{getDateAndTimeStamp(a.alertCreatedOn)}</b></span></div>
                                </div>
                                <hr />
                                <div>Facility Name: <b>({a.facID}) {a.facName}</b></div>
                                <div>Patient Name: <b>{a.patName}</b></div>
                                <div><span>Nursing Station: <b>{a.nsID}</b></span> <span className="ml-2">Room: <b>{a.room}</b></span></div>
                                <hr />
                                <div>Prescribed Drug: <b>{a.prescribedDrugName}</b></div>
                                <div>Drug Dispensed: <b>{a.drugLabelName}</b></div>
                                <div>Qty: <b>{a.qty}</b></div>
                                <div>Days Supply: <b>{a.daysSupply}</b></div>
                                <div>Total Price: <b>${a.ttlPrice}</b></div>
                                <hr />
                                {showNonMediprocityUserAlert && (
                                    <Alert color="danger" className="alert-link">
                                        SpecialtyRx Now credentials are required for user actions <br /> <br />
                                        <a href="/settings/specialtyRxNow">Set SpecialtyRx Now credentials</a>
                                    </Alert>
                                )}
                                <div className="bg-dark d-flex justify-content-end shadow" style={{ height: '45px', borderRadius: '3px' }}>
                                    {getActionButtonsByRolePermission('therapeutic_interchange', a?.hasOwnProperty('_internalArchiveID') && a._internalArchiveID > 0 ? archivedActionButtons : remainingWidgetsActionButtons, a?.pharmID, a?.facID)?.map((button, i) => (
                                        <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i + 'detail'} action={button.action} disabled={button.disable} />
                                    ))}
                                </div>
                            </>
                        )}
                        renderActionsBar={() => (
                            <>
                                {getActionButtonsByRolePermission('therapeutic_interchange', alert?.hasOwnProperty('_internalArchiveID') && alert._internalArchiveID > 0 ? archivedActionButtons : remainingWidgetsActionButtons, alert?.pharmID, alert?.facID)?.map((button, i) => (
                                    <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i} action={button.action} disabled={button.disable} />
                                ))}
                                <ActionQuestionModal isOpen={questionModalInfo.isOpen} action={questionModalInfo.action} toggle={toggleQuestionModal} color={questionModalInfo.color} label={questionModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={'TherapeuticInt'} />
                            </>
                        )}
                    />
                )}
                {stateName === 'admissionMedsList' && (
                    <AuthorizationDetailView
                        alert={alert}
                        activeTab={activeTab}
                        category={category}
                        getDateTime={getDateTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        renderDetailContent={(a) => (
                            <>
                                <div className="d-flex justify-content-end align-items-center">
                                    <div className="mr-auto">Admission Date: <b>{getDateTime(a.admDate)}</b></div>
                                </div>
                                <hr style={{ marginTop: '10px' }} />
                                <div>Facility Name: <b>({a.facID}) {a.facName}</b></div>
                                <div>Patient Name: <b>{a.patName}</b></div>
                                <div><span>Nursing Station: <b>{a.nsID}</b></span> <span className="ml-2">Room: <b>{a.room}</b></span> <span className="ml-2">Bed: <b>{a.bed}</b></span></div>
                                <hr />
                                <div className="d-flex justify-content-between align-items-end">
                                    <h4 className="ml-1">Admission Meds:</h4>
                                    <div className="d-flex flex-column align-items-end">
                                            <div className="mr-1">Total Meds: <b>{a.admissionMedsDetailsList?.length}</b></div>
                            {!a?.admissionMedsDetailsList?.some(line => line?.enablePerDiem === true) && (
  <div className="mr-1">
    Grand Total: $<b>{a.sumTtlPrice}</b>
  </div>
)}
                                    </div>
                                </div>
                                <div style={{ border: '2px solid #eef2f7', borderRadius: '5px' }}>
                                    <SimpleBar style={{ maxHeight: '330px' }} className="mx-1 px-1">
                                        {(a.admissionMedsDetailsList || []).map((line, i) => (
                                            <React.Fragment key={i}>
                                                <Card className="mb-0">
                                                    <CardHeader id={'medList' + i} className="bg-light admissionMedsAccordion" style={{ padding: '6px 12px 0px 12px' }}>
                                                        <span><b>{line.drugLabelName}</b></span>
                                                        <span className="float-right"><i className="mdi mdi-chevron-right font-18 accordion-arrow" /></span>
                                                    </CardHeader>
                                                    <UncontrolledCollapse toggler={'medList' + i}>
                                                        <CardBody>
                                                            <div>Drug: <b>{line.drugLabelName}</b></div>
                                                            <div>RX Number: <b>{line.rxNo}</b></div>
                                                            <div>Dispense Date: <b>{getDateTime(line.dispenseDt)}</b></div>
                                                            <div>Alerted On: <b>{getDateTime(a.admDate)}</b></div>
                                                            <div>Days Supply: <b>{line.daysSupply}</b></div>
                                                            <div>Is House Stock: <b>{line.isHouseStock == 'N' ? 'No' : 'Yes'}</b></div>
<div>
  Med available in Ebox or Cubex:{' '}
<b>  {line?.oss === true ? 'Yes' : line?.oss === false ? 'No' : ''} </b>
</div>                   
                                             {!line?.enablePerDiem && (
                                                            <div>Total Price: <b>${line.ttlPrice}</b></div> 
                                                             )} 
                                                        </CardBody>
                                                    </UncontrolledCollapse>
                                                </Card>
                                            </React.Fragment>
                                        ))}
                                    </SimpleBar>
                                </div>
                                <div className="bg-dark d-flex justify-content-end shadow" style={{ height: '45px', borderRadius: '3px' }}>
                                    {getActionButtonsByRolePermission('admission_meds', a?.hasOwnProperty('_internalArchiveID') && a._internalArchiveID > 0 ? archivedActionButtons : admissionMedicationReviewActionButtons, a?.pharmID, a?.facID)?.map((button, i) => (
                                        <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i + 'detail'} action={button.action} disabled={button.disable} />
                                    ))}
                                </div>
                            </>
                        )}
                        renderActionsBar={() => (
                            <>
                                {getActionButtonsByRolePermission('admission_meds', alert?.hasOwnProperty('_internalArchiveID') && alert._internalArchiveID > 0 ? archivedActionButtons : admissionMedicationReviewActionButtons, alert?.pharmID, alert?.facID)?.map((button, i) => (
                                    <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i} action={button.action} disabled={button.disable} />
                                ))}
                                <ActionQuestionModal isOpen={questionModalInfo.isOpen} action={questionModalInfo.action} toggle={toggleQuestionModal} color={questionModalInfo.color} label={questionModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={'AdmissionMeds'} />
                            </>
                        )}
                    />
                )}
                {stateName === 'openBatchList' && (
                    <AuthorizationDetailView
                        alert={alert}
                        activeTab={activeTab}
                        category={category}
                        getDateTime={getDateTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        getUserActionLabelForHeader={getUserActionLabelForHeader}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        renderDetailContent={(a) => (
                            <>
                                <div className="d-flex justify-content-between">
                                    <div><p>RX Number: </p><span><b>{a.rxNo}</b></span></div>
                                    <div><p>Dispense Date:</p><span><b>{getDateTime(a.dispenseDt)}</b></span></div>
                                    <div><p>Alerted On:</p><span><b>{getDateTime(a.alertCreatedOn)}</b></span><span className="ml-2"><b>{getDateAndTimeStamp(a.alertCreatedOn)}</b></span></div>
                                </div>
                                <hr />
                                <div>Facility Name: <b>({a.facID}) {a.facName}</b></div>
                                <div>Patient Name: <b>{a.patName}</b></div>
                                <div><span>Nursing Station: <b>{a.nsID}</b></span></div>
                                <hr />
                                <div>Drug: <b>{a.drugLabelName}</b></div>
                                <div>Qty: <b>{a.qty}</b></div>
                                <div>Days Supply: <b>{a.daysSupply}</b></div>
                                <div>Sig: <b>{decode(a.sig)}</b></div>
                                <hr />
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="ml-1">Comments:</h4>
                                    <div className="mr-1">Total: <b>{getAlertCommentsForDisplay(a).length}</b></div>
                                </div>
                                <div style={{ border: '2px solid #eef2f7', borderRadius: '5px' }}>
                                    <SimpleBar style={{ maxHeight: '330px' }} className="mx-1 px-1">
                                        {getAlertCommentsForDisplay(a).map((comment, i) => (
                                            <React.Fragment key={i}>
                                                <Card className={'mx-1', 'border', 'mb-1', 'shadow-sm', { 'mt-1': i === 0 }}>
                                                    <CardHeader className="bg-light">
                                                        {comment.reason ? (
                                                            <div className="mb-1"><b>{comment.reason}</b></div>
                                                        ) : null}
                                                        <div><small><i>{getDateAndTime(comment.loggedAt)}</i></small></div>
                                                    </CardHeader>
                                                    <CardBody><div>{comment.comment}</div></CardBody>
                                                </Card>
                                            </React.Fragment>
                                        ))}
                                    </SimpleBar>
                                </div>
                                {(a.srxStatus || a.pharmacyStatus) && <hr />}
                                {a.srxStatus && <h4 className="my-1">Resolution: {getUserActionLabelForHeader(a.srxStatus)}</h4>}
                                {a.pharmacyStatus && <small>Pharmacy Resolution: <b>{a.pharmacyStatus}</b></small>}
                                <hr />
                                {showNonMediprocityUserAlert && (
                                    <Alert color="danger" className="alert-link">
                                        SpecialtyRx Now credentials are required for user actions <br /> <br />
                                        <a href="/settings/specialtyRxNow">Set SpecialtyRx Now credentials</a>
                                    </Alert>
                                )}
                                <div className="bg-dark d-flex justify-content-end shadow" style={{ height: '45px', borderRadius: '3px' }}>
                                    {getActionButtonsByRolePermission('open_batch', a?.hasOwnProperty('_internalArchiveID') && a._internalArchiveID > 0 ? archivedActionButtons : clarificationsActionButtons, a?.pharmID, a?.facID)?.map((button, i) => (
                                        <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i + 'detail'} action={button.action} disabled={button.disable} />
                                    ))}
                                </div>
                            </>
                        )}
                        renderActionsBar={() => (
                            <>
                                {getActionButtonsByRolePermission('open_batch', alert?.hasOwnProperty('_internalArchiveID') && alert._internalArchiveID > 0 ? archivedActionButtons : clarificationsActionButtons, alert?.pharmID, alert?.facID)?.map((button, i) => (
                                    <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i} action={button.action} disabled={button.disable} />
                                ))}
                                <ActionSendModal isOpen={sendModalInfo.isOpen} action={sendModalInfo.action} toggle={toggleSendModal} color={sendModalInfo.color} label={sendModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={'openBatch'} />
                                <ActionDoNotSendModal isOpen={doNotSendModalInfo.isOpen} action={doNotSendModalInfo.action} toggle={toggleDoNotSendModal} color={doNotSendModalInfo.color} label={doNotSendModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={'openBatch'} />
                                <ActionQuestionModal isOpen={questionModalInfo.isOpen} action={questionModalInfo.action} toggle={toggleQuestionModal} color={questionModalInfo.color} label={questionModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={'openBatch'} />
                            </>
                        )}
                    />
                )}
                {category === 'refillTooSoon' && (
                    <AuthorizationDetailView
                        alert={alert}
                        activeTab={activeTab}
                        category={category}
                        getDateTime={getDateTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        getUserActionLabelForHeader={getUserActionLabelForHeader}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        renderDetailContent={(a) => (
                            <>
                                <div className="d-flex justify-content-between">
                                    <div><p>Last RX Number: </p><span><b>{a.rxNo}</b></span></div>
                                    <div><p>Last Fill Date:</p><span><b>{getDateTime(a.lastFillDt)}</b></span></div>
                                    <div><p>Alerted On:</p><span><b>{getDateTime(a.alertCreatedOn)}</b></span><span className="ml-2"><b>{getDateAndTimeStamp(a.alertCreatedOn)}</b></span></div>
                                </div>
                                <hr />
                                <div>Facility Name: <b>({a.facID}) {a.facName}</b></div>
                                <div>Patient Name: <b>{a.patName}</b></div>
                                <div><span>Nursing Station: <b>{a.nsID}</b></span> <span className="ml-2">Room: <b>{a.room}</b></span> <span className="ml-2">Bed: <b>{a.bed}</b></span></div>
                                <hr />
                                <div>Drug Label Name: <b>{a.drugLabelName}</b></div>
                                <div>Qty: <b>{a.qty}</b></div>
                                <div>Days Supply: <b>{a.daysSupply}</b></div>
                                <div>Due Date: <b>{getDateTime(a.dueDate)}</b></div>
                                {(a.srxStatus || a.pharmacyStatus) && <hr />}
                                {a.srxStatus && <h4 className="my-1">Resolution: {getUserActionLabelForHeader(a.srxStatus)}</h4>}
                                {a.pharmacyStatus && <small>Pharmacy Resolution: <b>{a.pharmacyStatus}</b></small>}
                                <hr />
                                {showNonMediprocityUserAlert && (
                                    <Alert color="danger" className="alert-link">
                                        SpecialtyRx Now credentials are required for user actions <br /> <br />
                                        <a href="/settings/specialtyRxNow">Set SpecialtyRx Now credentials</a>
                                    </Alert>
                                )}
                                <div className="bg-dark d-flex justify-content-end shadow" style={{ height: '45px', borderRadius: '3px' }}>
                                    {getActionButtonsByRolePermission('refill_too_soon', RTSActionButtons, a.pharmID, a.facID)?.map((button, i) => (
                                        <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i + 'detail'} action={button.action} disabled={button.disable} />
                                    ))}
                                </div>
                            </>
                        )}
                        renderActionsBar={() => (
                            <>
                                {getActionButtonsByRolePermission('refill_too_soon', RTSActionButtons, alert?.pharmID, alert?.facID)?.map((button, i) => (
                                    <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i} action={button.action} disabled={button.disable} />
                                ))}
                                <ActionQuestionModal isOpen={questionModalInfo.isOpen} action={questionModalInfo.action} toggle={toggleQuestionModal} color={questionModalInfo.color} label={questionModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={resolvedActionWidgetNames.refillTooSoon} />
                                <ActionCommentModal isOpen={commentModalInfo.isOpen} action={commentModalInfo.action} toggle={toggleCommentModal} color={commentModalInfo.color} label={commentModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={resolvedActionWidgetNames.refillTooSoon} warning="by approving until refill due, your facility is agreeing to pay the cost until the next refill" />
                            </>
                        )}
                    />
                )}
                {category === 'pumpPickup' && (
                    <AuthorizationDetailView
                        alert={alert}
                        activeTab={activeTab}
                        category={category}
                        getDateTime={getDateTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        getUserActionLabelForHeader={getUserActionLabelForHeader}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        renderDetailContent={(a) => (
                            <>
                                <div className="d-flex justify-content-between">
                                    {a.rxNo != a.pumpRxNo && <div><p>RX Number: </p><span><b>{a.rxNo}</b></span></div>}
                                    <div><p>Serial Number:</p><span><b>{a.drugLabelName} - {a.pumpSerialNo}</b></span></div>
                                    {
                                        a.pickupRequests > 0 ?
                                            <div><p>Last Requested On:</p><span><b>{getDateTime(a.lastPickupRequest)}</b></span></div>
                                            : (
                                                a.returnDt != null && <div><p>Returned On:</p><span><b>{getDateTime(a.returnDt)}</b></span></div>
                                                ||
                                                a.billedFinalPump && <div><p>Billed On:</p><span><b>{getDateTime(a.alertUpdatedOn)}</b></span></div>
                                                ||
                                                a.returnDueDt != null && <div><p>Return Due On:</p><span><b>{getDateTime(a.returnDueDt)}</b></span></div>
                                            )
                                    }
                                </div>
                                <hr />
                                <div>Facility Name: <b>({a.facID}) {a.facName}</b></div>
                                <div>Patient Name: <b>{a.patName}</b></div>
                                <div><span>Nursing Station: <b>{a.nsID}</b></span> <span className="ml-2">Room: <b>{a.room}</b></span> <span className="ml-2">Bed: <b>{a.bed}</b></span></div>
                                <div>Request #: <b>{a.pickupRequests}</b></div>
                                <div>PumpRx: <b>{a.pumpRxNo}</b></div>
                                <div>Delivered On: <b>{getDateTime(a.dispenseDt)}</b></div>
                                <div>Due On: <b>{getDateTime(a.returnDueDt)}</b></div>
                                <hr />
                                {a.rxLabelText && <div>Notes: <b>{a.rxLabelText}</b></div>}
                                {a.returnDt != null && <div>Returned On: <b>{getDateTime(a.returnDt)}</b></div>}
                                <hr />
                                {a.billedFinalPump && <h4 className="my-1">Non returned pump - billed on: <b>{getDateTime(a.alertUpdatedOn)}</b></h4>}
                                <hr />
                                {showNonMediprocityUserAlert && (
                                    <Alert color="danger" className="alert-link">
                                        SpecialtyRx Now credentials are required for user actions <br /> <br />
                                        <a href="/settings/specialtyRxNow">Set SpecialtyRx Now credentials</a>
                                    </Alert>
                                )}
                                <div className="bg-dark d-flex justify-content-end shadow" style={{ height: '45px', borderRadius: '3px' }}>
                                    {getActionButtonsByRolePermission('pump_pickup', pumpPickupActionButtons, a.pharmID, a.facID)?.map((button, i) => (
                                        <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i + 'detail'} action={button.action} disabled={button.disable} />
                                    ))}
                                </div>
                            </>
                        )}
                        renderActionsBar={() => (
                            <>
                                {getActionButtonsByRolePermission('pump_pickup', pumpPickupActionButtons, alert?.pharmID, alert?.facID)?.map((button, i) => (
                                    <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i} action={button.action} disabled={button.disable} />
                                ))}
                                <ActionQuestionModal isOpen={questionModalInfo.isOpen} action={questionModalInfo.action} toggle={toggleQuestionModal} color={questionModalInfo.color} label={questionModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={resolvedActionWidgetNames.pumpPickup} />
                                <ActionCommentModal isOpen={commentModalInfo.isOpen} action={commentModalInfo.action} toggle={toggleCommentModal} color={commentModalInfo.color} label={commentModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={resolvedActionWidgetNames.pumpPickup} />
                            </>
                        )}
                    />
                )}
                {(category === 'narcoticsRefill' || category === 'alfNewScript') && (
                    <AuthorizationDetailView
                        alert={alert}
                        activeTab={activeTab}
                        category={category}
                        getDateTime={getDateTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        getUserActionLabelForHeader={getUserActionLabelForHeader}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        renderDetailContent={(a) => (
                            <>

                                <div className="d-flex justify-content-between">
                                    <div>
                                        <div className="text-muted">Patient Name:</div>
                                        <div><b>{a.patName}</b></div>
                                    </div>
                                    <div>
                                        <div className="text-muted">Rx Number:</div>
                                        <div><b>{a.rxNo}</b></div>
                                    </div>
                                </div>
                                <div className="mt-1">
                                    <div className="text-muted">Drug Name:</div>
                                    <div><b>{a.drugLabelName}</b></div>
                                </div>
                                <div className="mt-1">
                                    <div className="text-muted">Directions:</div>
                                    <div><b>{a.sig}</b></div>
                                </div>
                                <hr />
                                <div>Facility Name: <b>({a.facID}) {a.facName}</b></div>
                                <div><span>Nursing Station: <b>{a.nsID}</b></span> <span className="ml-2">Room: <b>{a.room}</b></span> <span className="ml-2">Bed: <b>{a.bed}</b></span></div>
                                {category === 'alfNewScript' && (
                                    <>
                                        <div>Physician Name: <b>{a.patDoctor || ''}</b></div>
                                    </>
                                )}
                                <hr />
                                 {category === 'alfNewScript' && (
                                     <div>Qty: <b>{a.qty}</b></div> )}
                                     <div>Last Fill Date: <b>{getDateTime(a.lastFillDt)}</b></div>
                                <div>Dispense Qty: <b>{a.totalQtyDispensed}</b></div>
                                <div>Days Supply: <b>{a.daysSupply}</b></div>
                                <div>Total Qty Prescribed: <b>{a.totalQtyWritten}</b></div>
                                <div>Qty Remaining: <b>{a.qtyRemaining}</b></div>
                                {category === 'alfNewScript' && (
                                    <>

                                    <div> Rx Expiration: <b>{getDateTime(a.expireDt || '')}</b></div>
                                    <div>Rx Prescriber: <b>{a.prescribingDoctor || ''}</b></div>
                                     </>

                                )}
                                {(a.srxStatus || a.pharmacyStatus) && <hr />}
                                {a.srxStatus && <h4 className="my-1">Resolution: {getUserActionLabelForHeader(a.srxStatus)}</h4>}
                                {a.pharmacyStatus && <small>Pharmacy Resolution: <b>{a.pharmacyStatus}</b></small>}
                                <hr />
                                {showNonMediprocityUserAlert && (
                                    <Alert color="danger" className="alert-link">
                                        SpecialtyRx Now credentials are required for user actions <br /> <br />
                                        <a href="/settings/specialtyRxNow">Set SpecialtyRx Now credentials</a>
                                    </Alert>
                                )}
                                <div className="bg-dark d-flex justify-content-end shadow" style={{ height: '45px', borderRadius: '3px' }}>
                                    {getNarcoticsStyleDiscussButtons(a.pharmID, a.facID)?.map((button, i) => (
                                        <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i + 'detail'} action={button.action} disabled={button.disable} />
                                    ))}
                                </div>
                            </>
                        )}
                        renderActionsBar={() => (
                            <>
                                {getNarcoticsStyleDiscussButtons(alert?.pharmID, alert?.facID)?.map((button, i) => (
                                    <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i} action={button.action} disabled={button.disable} />
                                ))}
                                <ActionQuestionModal isOpen={questionModalInfo.isOpen} action={questionModalInfo.action} toggle={toggleQuestionModal} color={questionModalInfo.color} label={questionModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={resolvedActionWidgetNames.narcoticsDiscuss} />
                                <ActionCommentModal isOpen={commentModalInfo.isOpen} action={commentModalInfo.action} toggle={toggleCommentModal} color={commentModalInfo.color} label={commentModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={resolvedActionWidgetNames.narcoticsDiscuss} />
                            </>
                        )}
                    />
                )}
                {stateName === 'alfCostResponseList' && (
                    <AuthorizationDetailView
                        alert={alert}
                        activeTab={activeTab}
                        category={category}
                        getDateTime={getDateTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        renderDetailContent={(a) => (
                            <>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <p className="mb-1 text-muted">Drug Name:</p>
                                        <span><b>{a.drugLabelName}</b></span>
                                    </div>
                                    <div>
                                        <p className="mb-1 text-muted">Rx Number:</p>
                                        <span><b>{a.rxNo}</b></span>
                                    </div>
                                    <div>
                                        <p className="mb-1 text-muted">Med Status:</p>
                                        <Badge
    pill
    style={{
        backgroundColor:
            String(a.approvalStatus).toLowerCase() === 'denied'
                ? '#ffe2e2'
                : '#fef3c6',
        color:
            String(a.approvalStatus).toLowerCase() === 'denied'
                ? '#cd5a5c'
                : '#d26a00',
        fontSize: '13px',
        fontWeight: '600',
        padding: '6px 14px',
    }}>
    {a.approvalStatus
        ? String(a.approvalStatus).charAt(0).toUpperCase() +
          String(a.approvalStatus).slice(1).toLowerCase()
        : ''}
</Badge>
                                    </div>
                                </div>
                                <hr />
                                <div>Patient Name: <b>{a.patName}</b></div>
                                <div>Facility Name: <b>({a.facID}) {a.facName}</b></div>
                                <div>Physician Name: <b>{a.phName || ''}</b></div>
                                                                <div><span>Nursing Station: <b>{a.nsID} </b> </span> </div>
                                <div style={{padding:"10px 0px"}}>Directions: <b>{a.sig}</b></div>
                                <div>Date: <b>{getDateTime(a.dateCreated)}</b></div>
                                <div>Quantity: <b>{a.qty}</b></div>
                                <div>Days Supply: <b>{a.daysSupply}</b></div>
                            </>
                        )}
                    />
                )}
                {stateName === 'unavailableMedicationAlertsList' && (
                    <AuthorizationDetailView
                        alert={alert}
                        activeTab={activeTab}
                        category={category}
                        getDateTime={getDateTime}
                        getDateAndTimeStamp={getDateAndTimeStamp}
                        showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                        renderDetailContent={(a) => (
                            <>
                                <div className="d-flex justify-content-between">
                                    <div><p className="mb-1 text-muted">Drug Name:</p><span><b>{a.drugLabelName}</b></span></div>
                                    <div><p className="mb-1 text-muted">Rx Number:</p><span><b>{a.rxNo}</b></span></div>
                                    <div>
                                        <p className="mb-1 text-muted">Med Status:</p>
                                        {(() => {
                                            const resolvedStatus =
                                                a.availabilityStatus ??
                                                a.AvailabilityStatus ??
                                                a.status ??
                                                a.Status ??
                                                a.medStatus ??
                                                a.MedStatus;
                                            const isBackorder = String(resolvedStatus).toUpperCase() === 'BACKORDER';
                                            return (
                                                <Badge
                                                    style={{
                                                        backgroundColor: isBackorder ? '#fb2c36' : '#fdc700',
                                                        color: isBackorder ? '#fff' : '#000',
                                                        fontWeight: 600,
                                                        padding: '6px 10px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        fontSize: '15px',
                                                    }}>
                                                    {resolvedStatus ? toTitleCase(resolvedStatus) : ''}
                                                </Badge>
                                            );
                                        })()}
                                    </div>
                                </div>
                                <hr />
                                <div>Facility Name: <b>({a.facID}) {a.facName}</b></div>
                                <div>Patient Name: <b>{a.patName}</b></div>
                                <div><span>Nursing Station: <b>{a.nsID}</b></span> <span className="ml-2">Room: <b>{a.room}</b></span> <span className="ml-2">Bed: <b>{a.bed}</b></span></div>
                                <div>Physician Name: <b>{a.phName || ''}</b></div>
                                <hr />
                                <div>Directions: {renderEmphasizedLines(a.sig)}</div>
                                <hr />
                                <div>Dispense Date: <b>{a.dispenseDt ? getDateTime(a.dispenseDt) : ''}</b></div>
                                <div>Quantity: <b>{a.qty}</b></div>
                                <div>Days Supply: <b>{a.daysSupply}</b></div>
                                {(a.comment ?? a.Comment) && (
                                    <div>
                                        Notes: {renderEmphasizedLines(a.comment ?? a.Comment)}
                                    </div>
                                )}
                                <hr />
                                {showNonMediprocityUserAlert && (
                                    <Alert color="danger" className="alert-link">
                                        SpecialtyRx Now credentials are required for user actions <br /> <br />
                                        <a href="/settings/specialtyRxNow">Set SpecialtyRx Now credentials</a>
                                    </Alert>
                                )}
                                <div className="bg-dark d-flex justify-content-end shadow" style={{ height: '45px', borderRadius: '3px' }}>
                                    {getActionButtonsByRolePermission('specialty_med_or_not_available', remainingWidgetsActionButtons, a.pharmID, a.facID)?.map((button, i) => (
                                        <ActionButton key={i + 'detail'} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i + 'detail'} action={button.action} disabled={button.disable} />
                                    ))}
                                </div>
                            </>
                        )}
                        renderActionsBar={() => (
                            <>
                                {getActionButtonsByRolePermission('specialty_med_or_not_available', remainingWidgetsActionButtons, alert?.pharmID, alert?.facID)?.map((button, i) => (
                                    <ActionButton key={i} handleOnClick={handleActionButtons} icon={button.icon} title={button.title} index={i} action={button.action} disabled={button.disable} />
                                ))}
                                <ActionQuestionModal isOpen={questionModalInfo.isOpen} action={questionModalInfo.action} toggle={toggleQuestionModal} color={questionModalInfo.color} label={questionModalInfo.label} submitAction={handleUserAction} loading={loadingUserAction} widget={'UnavailableMedicationAlerts'} />
                            </>
                        )}
                    />
                )}
        </>
    );
};

export default TableRowDetailBody;
